declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((command: string, ...args: unknown[]) => void) & { push?: (...args: unknown[]) => void };
  }
}

const GOOGLE_ADS_LEAD_CONVERSIONS = [
  'AW-18495941387/RE4dCMmTqZMdEIvWxvNE',
  'AW-18495941387/4JvDCN3Dl5IdEIvWxvNE',
];

export function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : undefined;
}

export function getOrCreateFbp(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const existing = getCookie('_fbp');
  if (existing) return existing;
  const generated = `fb.1.${Date.now()}.${Math.floor(1000000000 + Math.random() * 9000000000)}`;
  document.cookie = `_fbp=${generated}; path=/; max-age=7776000; SameSite=Lax`;
  return generated;
}

export function getOrCreateFbc(): string | undefined {
  if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');
    if (fbclid) {
      const existing = getCookie('_fbc');
      if (existing && existing.endsWith(`.${fbclid}`)) {
        return existing;
      }
      const generated = `fb.1.${Date.now()}.${fbclid}`;
      document.cookie = `_fbc=${generated}; path=/; max-age=7776000; SameSite=Lax`;
      return generated;
    }
  } catch {
    // ignore URL parsing errors
  }
  return getCookie('_fbc');
}

export function sendMetaCapi(eventName: string, eventId: string, customData?: Record<string, unknown>): void {
  const payload = {
    event_name: eventName,
    event_id: eventId,
    event_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
    fbp: getOrCreateFbp(),
    fbc: getOrCreateFbc(),
    client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    custom_data: customData,
  };
  const endpoint = '/api/api/meta-capi';
  fetch(endpoint, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

const META_STANDARD_EVENTS = new Set([
  'PageView',
  'ViewContent',
  'Search',
  'AddToCart',
  'InitiateCheckout',
  'AddPaymentInfo',
  'Purchase',
  'Lead',
  'CompleteRegistration',
  'Contact',
  'CustomizeProduct',
  'Donate',
  'FindLocation',
  'Schedule',
  'StartTrial',
  'SubmitApplication',
  'Subscribe',
]);

function trackMeta(
  eventName: string,
  customData?: Record<string, unknown>,
  isCustom = false,
  explicitEventId?: string
): string {
  const isStandard = !isCustom && META_STANDARD_EVENTS.has(eventName);
  const eventId = explicitEventId || `${eventName.toLowerCase()}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  window.fbq?.(isStandard ? 'track' : 'trackCustom', eventName, customData, { eventID: eventId });
  sendMetaCapi(eventName, eventId, customData);
  return eventId;
}

export function trackPageView(
  path = typeof window !== 'undefined' ? window.location.pathname : '/',
  title = typeof document !== 'undefined' ? document.title : ''
): void {
  if (typeof window === 'undefined') return;
  window.gtag?.('event', 'page_view', { page_path: path, page_title: title, page_location: window.location.href });
  trackMeta('PageView');
}

export function trackEvent(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.gtag?.('event', eventName, params);
  trackMeta(eventName, params, !META_STANDARD_EVENTS.has(eventName));
}

export function trackWhatsAppClick(location: string, service?: string): void {
  if (typeof window === 'undefined') return;
  const params: Record<string, unknown> = {
    event_category: 'whatsapp_lead',
    event_label: location,
    location,
    ...(service ? { service } : {}),
  };
  window.gtag?.('event', 'whatsapp_click', params);
  GOOGLE_ADS_LEAD_CONVERSIONS.forEach((send_to) => window.gtag?.('event', 'conversion', { send_to }));
  const leadData: Record<string, unknown> = {
    content_name: service ? `WhatsApp - ${service}` : 'WhatsApp Lead',
    content_category: location,
  };
  const eventId = trackMeta('Lead', leadData);
  trackMeta('whatsapp_click', params, true, eventId);
}

export function trackEmailClick(location = 'direct'): void {
  if (typeof window === 'undefined') return;
  const params: Record<string, unknown> = { event_category: 'contact', event_label: location, location };
  window.gtag?.('event', 'email_click', params);
  const eventId = trackMeta('Contact', { content_category: location });
  trackMeta('email_click', params, true, eventId);
}

export function trackInstagramClick(): void {
  if (typeof window === 'undefined') return;
  const params: Record<string, unknown> = { event_category: 'social', event_label: 'instagram' };
  window.gtag?.('event', 'instagram_click', params);
  trackMeta('instagram_click', params, true);
}

export function trackServiceView(serviceId: string, serviceTitle: string): void {
  if (typeof window === 'undefined') return;
  window.gtag?.('event', 'view_item', {
    items: [{ item_id: serviceId, item_name: serviceTitle }],
    service_id: serviceId,
    service_title: serviceTitle,
  });
  const viewData: Record<string, unknown> = {
    content_name: serviceTitle,
    content_ids: [serviceId],
    content_type: 'product',
  };
  const eventId = trackMeta('ViewContent', viewData);
  trackMeta('service_view', { service_id: serviceId, service_title: serviceTitle }, true, eventId);
}

export function trackSelectContent(contentType: string, itemId: string): void {
  if (typeof window === 'undefined') return;
  const params: Record<string, unknown> = { content_type: contentType, item_id: itemId };
  window.gtag?.('event', 'select_content', params);
  trackMeta('service_click', params, true);
}

export function trackLanguageChange(language: string): void {
  trackEvent('language_change', { language });
}

export function track404PageView(path: string): void {
  trackEvent('page_view_404', { path });
}

export function logEvent(_analytics: unknown, eventName: string, params?: Record<string, unknown>): void {
  trackEvent(eventName, params);
}

export const analytics: unknown = null;
